import React, { useState } from "react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import TextArea from "./common/form/input/TextArea";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  rows?: number;
}

/**
 * Markdown editor with Write/Preview toggle.
 * Allows users to write markdown and preview the rendered output.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter description (supports markdown)",
  label,
  rows = 5,
}) => {
  const [mode, setMode] = useState<"write" | "preview">("write");

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {/* Toggle tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mode === "write"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 dark:text-white"
          }`}
          onClick={() => setMode("write")}
        >
          Write
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mode === "preview"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
          onClick={() => setMode("preview")}
        >
          Preview
        </button>
      </div>

      {/* Content */}
      {mode === "write" ? (
        <TextArea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
        />
      ) : (
        <div
          className="min-h-[120px] rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          style={{ minHeight: `${rows * 1.5 + 2}rem` }}
        >
          {value ? (
            <MarkdownRenderer content={value} />
          ) : (
            <p className="text-sm italic text-gray-400 dark:text-gray-500">
              Nothing to preview
            </p>
          )}
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Supports markdown: **bold**, *italic*, - lists, # headings, [links](url)
      </p>
    </div>
  );
};
